<script setup>
import { onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import PixtaAPI from 'custom/api/pixta';
import { useLocoI18n } from 'custom/composables/useLocoI18n';
import { usePixtaConnection } from './usePixtaConnection';

// Settings › Pixta: the address and key of Pixta's Support API (the key is never sent back, only its last digits)
const { t } = useLocoI18n();
const { refresh } = usePixtaConnection();

const current = ref(null);
const form = reactive({ base_url: '', api_key: '', enabled: true });
const isSaving = ref(false);
const isTesting = ref(false);

const errorMessage = error => error.response?.data?.error || error.message;

const apply = data => {
  current.value = data;
  form.base_url = data.base_url || 'https://api.pixta.me';
  form.enabled = data.configured ? data.enabled : true;
  form.api_key = '';
};

const save = async () => {
  isSaving.value = true;
  try {
    const { data } = await PixtaAPI.updateConnection(form);
    apply(data);
    refresh();
    useAlert(t('LOCO.PIXTA.SETTINGS.SAVED'));
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isSaving.value = false;
  }
};

const test = async () => {
  isTesting.value = true;
  try {
    await PixtaAPI.testConnection();
    useAlert(t('LOCO.PIXTA.SETTINGS.TEST_OK'));
  } catch (error) {
    useAlert(errorMessage(error));
  } finally {
    isTesting.value = false;
  }
};

const disconnect = async () => {
  await PixtaAPI.deleteConnection();
  apply({ configured: false });
  refresh();
};

onMounted(async () => {
  const { data } = await PixtaAPI.connection();
  apply(data);
});
</script>

<template>
  <div class="w-full h-full overflow-y-auto bg-n-surface-1">
    <div class="max-w-3xl mx-auto px-6 py-8 flex flex-col gap-8">
      <header class="flex flex-col gap-1">
        <h1 class="text-heading-1 text-n-slate-12">
          {{ t('LOCO.PIXTA.SETTINGS.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-11">
          {{ t('LOCO.PIXTA.SETTINGS.SUBTITLE') }}
        </p>
      </header>

      <form v-if="current" class="flex flex-col gap-6" @submit.prevent="save">
        <div class="grid gap-4 md:grid-cols-2">
          <label class="flex flex-col gap-1 text-sm">
            <span class="font-medium text-n-slate-12">
              {{ t('LOCO.PIXTA.SETTINGS.BASE_URL') }}
            </span>
            <input v-model="form.base_url" type="url" required class="!mb-0" />
          </label>
          <label class="flex flex-col gap-1 text-sm">
            <span class="font-medium text-n-slate-12">
              {{ t('LOCO.PIXTA.SETTINGS.API_KEY') }}
            </span>
            <input
              v-model="form.api_key"
              type="password"
              autocomplete="off"
              class="!mb-0"
              :required="!current.configured"
              :placeholder="
                current.configured
                  ? t('LOCO.PIXTA.SETTINGS.KEY_SAVED', {
                      last4: current.api_key_last4,
                    })
                  : 'sup_key_…'
              "
            />
            <span class="text-xs text-n-slate-11">
              {{ t('LOCO.PIXTA.SETTINGS.API_KEY_HELP') }}
            </span>
          </label>
        </div>
        <label class="flex items-start justify-between gap-4 text-sm">
          <span class="flex flex-col gap-0.5">
            <span class="font-medium text-n-slate-12">
              {{ t('LOCO.PIXTA.SETTINGS.ENABLED') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t('LOCO.PIXTA.SETTINGS.ENABLED_HELP') }}
            </span>
          </span>
          <Switch v-model="form.enabled" />
        </label>
        <div class="flex flex-wrap gap-2">
          <Button
            type="submit"
            :label="t('LOCO.PIXTA.SETTINGS.SAVE')"
            :is-loading="isSaving"
          />
          <Button
            v-if="current.configured"
            type="button"
            faded
            slate
            :label="t('LOCO.PIXTA.SETTINGS.TEST')"
            :is-loading="isTesting"
            @click="test"
          />
          <Button
            v-if="current.configured"
            type="button"
            ghost
            ruby
            :label="t('LOCO.PIXTA.SETTINGS.DISCONNECT')"
            @click="disconnect"
          />
        </div>
      </form>
    </div>
  </div>
</template>
